import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { votingAPI } from '../services/api';

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0, -30px, 0);
  }
  70% {
    transform: translate3d(0, -15px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
`;

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  text-align: center;
  color: #1f2937;
  margin-bottom: 2rem;
`;

const WinnerCard = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 15px;
  padding: 2rem;
  margin: 1rem 0;
  text-align: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
`;

const Trophy = styled.div`
  fontSize: 4rem;
  animation: ${bounce} 2s infinite;
  margin-bottom: 1rem;
`;

const WinnerName = styled.h2`
  color: #1f2937;
  fontSize: 2rem;
  margin-bottom: 0.5rem;
`;

const Category = styled.h3`
  color: #6b7280;
  fontSize: 1.2rem;
  margin-bottom: 1rem;
`;

const Nickname = styled.p`
  font-style: italic;
  color: #374151;
  fontSize: 1.1rem;
  margin-bottom: 1rem;
`;

const VoteCount = styled.p`
  color: #6b7280;
  fontWeight: 600;
`;

const BackButton = styled.button`
  background: #667eea;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: #5a67d8;
    transform: translateY(-1px);
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #6b7280;
  fontSize: 1.2rem;
  padding: 2rem;
`;

interface Winner {
  id: number;
  category_name: string;
  nominee_name: string;
  user_name: string;
  vote_count: number;
  nicknames?: string;
  photo_url?: string;
}

const Results: React.FC = () => {
  const [winners, setWinners] = useState<Winner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const roomId = searchParams.get('room_id');

  const loadResults = useCallback(async () => {
    if (!roomId) return;
    try {
      setLoading(true);
      const results = await votingAPI.getResults(parseInt(roomId));
      console.log('Resultados obtenidos:', results);
      setWinners(results || []);
    } catch (error) {
      console.error('Error cargando resultados:', error);
      setWinners([]);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (roomId) {
      loadResults();
    }
  }, [roomId, loadResults]);

  if (!roomId) {
    return (
      <Container>
        <Card>
          <Title>Error</Title>
          <p>No se especificó la sala. Por favor regresa al dashboard.</p>
          <BackButton onClick={() => navigate('/dashboard')}>
            Volver al Dashboard
          </BackButton>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Card>
          <LoadingMessage>
            🏆 Cargando resultados increíbles...
          </LoadingMessage>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <BackButton onClick={() => navigate(`/dashboard?room_id=${roomId}`)}>
          ← Volver al Dashboard
        </BackButton>
        
        <Title>🏆 ¡Resultados Finales!</Title>
        
        {winners.length === 0 ? (
          <LoadingMessage>
            Aún no hay resultados disponibles. ¡Sé el primero en votar!
          </LoadingMessage>
        ) : (
          winners.map((winner) => (
            <WinnerCard key={winner.id}>
              <Trophy>🏆</Trophy>
              <Category>{winner.category_name}</Category>
              <WinnerName>{winner.user_name}</WinnerName>
              {winner.nicknames && (
                <Nickname>"{winner.nicknames}"</Nickname>
              )}
              <VoteCount>{winner.vote_count} votos</VoteCount>
            </WinnerCard>
          ))
        )}
      </Card>
    </Container>
  );
};

export default Results;